"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, HardHat, Award, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function StoryContent() {
  const prefersReducedMotion = useReducedMotion();
  const {
    posts,
    loading
  } = usePosts();
  const matchingPhoto = posts?.find((p: any) => {
    const photoName = p?.customFields?.photo_name || p?.acf?.photo_name || p?.title;
    return photoName === "Homepage Family Story Image";
  });
  const rawImg = matchingPhoto?.customFields?.image || (matchingPhoto as any)?.acf?.image;
  const imageSrc = (typeof rawImg === "string" ? rawImg : rawImg?.url) || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/scaffolding_team_on_site.png";
  const rawAlt = matchingPhoto?.customFields?.alt_text || (matchingPhoto as any)?.acf?.alt_text;
  const imageAlt = rawAlt || "BMF Scaffolding team on site wearing safety PPE and inspecting scaffold access framework";
  const isDynamicImage = Boolean(rawImg);
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: prefersReducedMotion ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 18
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  const accreditations = [{
    name: "NASC",
    detail: "National Access & Scaffolding Confederation"
  }, {
    name: "CHAS",
    detail: "Contractors Health and Safety Assessment"
  }];
  return <section id="family-business-story" data-nav="light" className="bg-background text-foreground py-16 md:py-24 lg:py-28 border-b border-border overflow-hidden">
      <div className="container mx-auto px-5 md:px-8 lg:px-14 max-w-7xl">
        {/* Category Datum Notch & Eyebrow */}
        <div className="flex flex-col items-start gap-2 mb-10 md:mb-14">
          <div className="h-1 w-12 bg-primary rounded-full" />
          <span className="text-xs md:text-sm font-bold uppercase tracking-wider text-muted-foreground">
            Our Heritage & Values
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold font-default tracking-tight text-foreground max-w-3xl leading-tight">
            Built on frontline experience and family values
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mt-1">
            Over twenty years on the tools, delivering safe, certified scaffolding with direct accountability across the South East.
          </p>
        </div>

        {/* Main Grid: Narrative Content + Genuine Photo & Legacy Module */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Left Column: Story & Principles (7 Cols) */}
          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} className="lg:col-span-7 flex flex-col gap-6">
            <motion.div variants={itemVariants} className="space-y-4">
              <h3 className="text-xl sm:text-2xl font-bold text-foreground">
                The care of a family business, the muscle of a commercial contractor
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                BMF Scaffolding was founded on a direct, hands-on commitment: every structure is engineered for safety, erected correctly the first time, and backed by people who answer their phones. Led by Pete Wood, who brings more than 20 years of direct site experience across commercial developments and private builds, our team approaches every handover with operational discipline.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                We carry forward the founding legacy of Adam Story, honouring a lasting reputation for craft, safety, and honest dealings. As we have grown to support main contractors, roofing specialists, solar installers, and local councils across Berkshire, Surrey, Hampshire, Buckinghamshire, and Greater London, our standard remains unchanged.
              </p>
            </motion.div>

            {/* Core Pillars */}
            <motion.div variants={itemVariants} className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              <div className="border border-border rounded-lg p-5 bg-card flex flex-col gap-2">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-md bg-accent text-primary">
                    <HardHat className="w-5 h-5" />
                  </div>
                  <h4 className="font-semibold text-foreground text-base">
                    Pete Wood
                  </h4>
                </div>
                <p className="text-xs text-muted-foreground">Director</p>
                <p className="text-sm text-muted-foreground leading-normal mt-1">
                  20+ years of frontline scaffolding leadership, guiding site safety inspections, crew training, and rigorous operational control.
                </p>
              </div>

              <div className="border border-border rounded-lg p-5 bg-card flex flex-col gap-2">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-md bg-accent text-primary">
                    <Award className="w-5 h-5" />
                  </div>
                  <h4 className="font-semibold text-foreground text-base">
                    Adam Story Legacy
                  </h4>
                </div>
                <p className="text-xs text-muted-foreground">Founding Legacy</p>
                <p className="text-sm text-muted-foreground leading-normal mt-1">
                  Carrying forward a respected heritage of reliability, trusted contractor partnerships, and absolute safety across the South East.
                </p>
              </div>
            </motion.div>

            {/* Verified Accreditation Badges */}
            <motion.div variants={itemVariants} className="pt-2">
              <div className="border-t border-border pt-5">
                <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-primary" /> Confirmed industry accreditations
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {accreditations.map((item, i) => <div key={item.name} data-index={i} className="border border-border rounded-md p-3.5 bg-muted/40 flex flex-col justify-between">
                      <span className="font-bold text-foreground text-sm tracking-wide">
                        {item.name}
                      </span>
                      <span className="text-xs text-muted-foreground mt-1">
                        {item.detail}
                      </span>
                    </div>)}
                </div>
              </div>
            </motion.div>

            {/* CTA Group */}
            <motion.div variants={itemVariants} className="pt-3 flex flex-wrap items-center gap-4">
              <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90">
                <Link to="/ContactRequestQuotePage" className="inline-flex items-center gap-2">
                  <span>Discuss a project</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="border-border text-foreground hover:bg-accent">
                <Link to="/AboutBmfPage">
                  <span>Read our full story</span>
                </Link>
              </Button>
            </motion.div>
          </motion.div>

          {/* Right Column: Authentic Photography & Site Standard Card (5 Cols) */}
          <motion.div initial={prefersReducedMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.5,
          ease: [0.16, 1, 0.3, 1]
        }} className="lg:col-span-5 flex flex-col gap-5">
            {/* Visual Frame */}
            <div className="relative rounded-lg overflow-hidden border border-border bg-muted shadow-sm group">
              {loading ? <div className="w-full aspect-[4/3] bg-muted animate-pulse" /> : <img src={imageSrc} data-wvc-srcset={isDynamicImage ? "" : "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/scaffolding_team_on_site.png-wvc-srcset"} data-wvc-sizes={isDynamicImage ? "" : "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/scaffolding_team_on_site.png-wvc-sizes"} alt={imageAlt} loading="lazy" decoding="async" className="w-full aspect-[4/3] object-cover object-center" />}
              <div className="absolute inset-0 bg-gradient-to-t from-secondary/80 via-transparent to-transparent pointer-events-none" />
              <div className="absolute bottom-4 left-4 right-4 text-secondary-foreground">
                <div className="inline-flex items-center gap-2 bg-secondary/90 px-3 py-1 rounded-md text-xs font-semibold backdrop-blur-sm border border-secondary-foreground/10">
                  <CheckCircle2 className="w-3.5 h-3.5 text-primary" />
                  <span>On-site safety compliance</span>
                </div>
                <p className="text-xs text-secondary-foreground/90 mt-2">
                  Direct management supervision on every residential, commercial, and solar installation.
                </p>
              </div>
            </div>

            {/* Operational Standards Card */}
            <Card className="border border-border shadow-xs bg-card">
              <CardContent className="p-5 sm:p-6 flex flex-col gap-3">
                <h4 className="font-bold text-foreground text-sm uppercase tracking-wider text-muted-foreground">
                  Our operational guarantee
                </h4>
                <div className="space-y-2.5 text-sm text-muted-foreground">
                  <div className="flex items-start gap-2.5">
                    <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    <span>Every handover accompanied by formal safety inspection tags</span>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    <span>Fully insured operations with TG20:21 compliance as standard</span>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    <span>Direct founder point-of-contact for quotes and technical site visits</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>;
}
export default function Story() {
  const wp_query_params = {
    post_type: "site_photo",
    per_page: 100,
    status: ["publish"]
  };
  const wp_query = new WP_Query(wp_query_params);
  return <PostsProvider wp_query={{
    post_type: "site_photo",
    per_page: 100,
    status: ["publish"]
  }}>
      <StoryContent />
    </PostsProvider>;
}